import {
  faArrowAltCircleUp,
  faCalendarCheck,
  faCalendarMinus,
  faCalendarTimes,
  faClipboard,
  faLineChart,
  faUser,
  faUsers,
  faClinicMedical
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext } from 'react';

import { useAppointmentAttendanceInsights } from '@/hooks/useAppointments';
import PersonContext from '@/pages/Person/PersonContext';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import MetricCard from '@/ui/components/MetricCard/MetricCard';

const AppointmentAttendanceInsights = () => {
  const { person } = useContext(PersonContext);

  const { data: insights, isPending: isLoadingInsights } = useAppointmentAttendanceInsights(
    person.id
  );

  if (isLoadingInsights) {
    return <LoadingSkeleton spinnerProps={{ label: 'Loading Appointment Data Insights...' }} />;
  }

  if (!insights) {
    return null;
  }

  const {
    attendance_streak,
    attended_appointments_count,
    attended_group_appointments_count,
    attended_individual_appointments_count,
    attended_medical_appointments_count,
    cancelled_appointments_count,
    missed_appointments_count,
    missed_group_appointments_count,
    missed_individual_appointments_count,
    missed_medical_appointments_count,
    total_appointments_count
  } = insights;

  const other_attended_appointments_count =
    attended_appointments_count -
    attended_group_appointments_count -
    attended_individual_appointments_count -
    attended_medical_appointments_count;
  const other_missed_appointments_count =
    missed_appointments_count -
    missed_group_appointments_count -
    missed_individual_appointments_count -
    missed_medical_appointments_count;

  return (
    <section className="w-full">
      <div className="cursor-default flex gap-2 justify-center text-2xl w-full">
        <MetricCard
          icon={<FontAwesomeIcon className="text-yellow-600" icon={faLineChart} />}
          metricName="Total Appointments"
          metricValue={total_appointments_count}
          tooltip="Total appointments that were scheduled for this member (includes drug screens)."
        />

        <MetricCard
          icon={<FontAwesomeIcon className="text-gray-500" icon={faCalendarTimes} />}
          metricName="Canceled"
          metricValue={cancelled_appointments_count}
          tooltip="Total appointments that were canceled by this member (includes drug screens)."
        />

        <MetricCard
          icon={<FontAwesomeIcon className="text-blue-600" icon={faArrowAltCircleUp} />}
          metricName="Attendance Streak"
          metricValue={attendance_streak}
          tooltip="The current streak for attending appointments."
        />
      </div>

      <section className="my-3">
        <div className="flex items-center mb-1">
          <FontAwesomeIcon className="text-emerald-600 mr-2" icon={faCalendarCheck} />
          <Heading value={3} text="Attended" />
        </div>

        <div className="cursor-default gap-2 justify-center text-2xl w-full grid grid-cols-2 sm:grid-cols-5">
          <MetricCard
            icon={<FontAwesomeIcon className="text-emerald-600" icon={faLineChart} />}
            metricName="Total"
            metricValue={attended_appointments_count}
            tooltip="Total appointments that were attended by this member (includes drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-emerald-600" icon={faUsers} />}
            metricName="Group"
            metricValue={attended_group_appointments_count}
            tooltip="Total group appointments that were attended by this member (does not include drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-emerald-600" icon={faUser} />}
            metricName="Individual"
            metricValue={attended_individual_appointments_count}
            tooltip="Total individual appointments that were attended by this member (does not include drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-emerald-600" icon={faClinicMedical} />}
            metricName="Medical"
            metricValue={attended_medical_appointments_count}
            tooltip="Total medical appointments that were attended by this member (does not include drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-emerald-600" icon={faClipboard} />}
            metricName="Other"
            metricValue={other_attended_appointments_count}
            tooltip="Total other appointments that were attended by this member (includes drug screens)."
          />
        </div>
      </section>

      <section>
        <div className="flex items-center mb-1">
          <FontAwesomeIcon className="text-red-600 mr-2" icon={faCalendarMinus} />
          <Heading value={3} text="Not Attended" />
        </div>

        <div className="cursor-default gap-2 justify-center text-2xl w-full grid grid-cols-2 sm:grid-cols-5">
          <MetricCard
            icon={<FontAwesomeIcon className="text-red-600" icon={faLineChart} />}
            metricName="Total"
            metricValue={missed_appointments_count}
            tooltip="Total appointments that were missed by this member (includes drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-red-600" icon={faUsers} />}
            metricName="Group"
            metricValue={missed_group_appointments_count}
            tooltip="Total group appointments that were missed by this member (does not include drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-red-600" icon={faUser} />}
            metricName="Individual"
            metricValue={missed_individual_appointments_count}
            tooltip="Total individual appointments that were missed by this member (does not include drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-red-600" icon={faClinicMedical} />}
            metricName="Medical"
            metricValue={missed_medical_appointments_count}
            tooltip="Total medical appointments that were missed by this member (does not include drug screens)."
          />

          <MetricCard
            icon={<FontAwesomeIcon className="text-red-600" icon={faClipboard} />}
            metricName="Other"
            metricValue={other_missed_appointments_count}
            tooltip="Total other appointments that were missed by this member (includes drug screens)."
          />
        </div>
      </section>
    </section>
  );
};

export default AppointmentAttendanceInsights;
